/**
 * Analytics — the gate, and nothing else.
 *
 * **Measured hosts: the free web build and, since 1 October 2026, the Android TWA.** The "no
 * tracking" claim was dropped for the App on that day (specs/analytics.md header) so app installs
 * can be a campaign target and in-app usage can be measured; public/privacy.html §3 and
 * marketing/data-safety.md declare it. The paid iOS app and the App Clip are still **not**
 * measured — that needs its own decision (App Store privacy label, and no data may reach an ad
 * network from there without ATT).
 *
 * In the TWA only GA4 loads — never an ad network's tag — and the game stays fully playable
 * offline: every load below is fire-and-forget, the chunks are kept out of the precache, and an
 * offline session simply sends nothing.
 *
 * Three questions, deliberately separate. Conflating the first two is how an ad network ends up
 * with organic traffic, or a paid app ends up with a tag.
 *
 *  1. **May we measure at all?** `mayMeasure()` — platform `web` or `android`. This is a question
 *     about the *host*, not the URL, and that is what makes it safe: the iOS shells inject
 *     `__ABCS_PLATFORM__` before any page JS runs (src/platform/index.ts), so the iOS app and the
 *     App Clip answer no on the very first frame. No URL anyone can type puts them on the
 *     measured side.
 *  2. **Is this the ad demo?** `isDemoSession()` — platform `web` AND `?demo`. Narrower, and it
 *     decides two things: whether an ad network's tag may load at all, and the value of the `demo`
 *     parameter riding on every engagement event. It stays platform-checked as well as
 *     flag-checked, because `isClipMode()` would NOT be tight enough — that is also true inside
 *     the App Clip and under the `?clip` review flag.
 *  3. **Whose ad tag, on top?** `adNetwork()` — the network that sent this visitor, from
 *     `utm_source` with the platforms' own click ids as a fallback. A demo session loads at most
 *     ONE of these: loading two would let each network see and cookie the other's traffic and
 *     would make every demo visitor pay for two trackers. A visitor with no recognisable source —
 *     a shared `?demo` link, an App Review URL, us testing — gets **no ad tag at all**. That is
 *     the intended default, not an oversight.
 *
 * GA4 is not an ad network and the "never two" rule does not reach it: it is our own property, so
 * it neither hands a competitor's traffic over nor bids on anything. It is the only tag an
 * ordinary web visit ever loads.
 *
 * **The ad tags never widen with GA4.** An ad network's business is "did this campaign produce a
 * buyer", so it only ever sees the sessions a campaign paid for. Organic web traffic is ours to
 * measure and nobody else's — which is why the ad tags keep their own list below, separate from
 * GA4's sink, rather than everything sharing one `trackers` array as it did before.
 *
 * **Tags are lazy-loaded** (`await import`), which is the point of this file existing separately
 * from the per-network modules. Vite emits each as its own chunk, so the iOS app and the App Clip
 * download *zero bytes* of tracker code, and the TWA only GA4's, rather than shipping an inert copy
 * to everyone and trusting a runtime `if`. Keep this module free of any static import of them, and keep the chunks out of the
 * service-worker precache (see vite.config.ts `globIgnores`).
 *
 * The three events, chosen as a funnel a campaign can bid on:
 *   page view         — the page opened
 *   trial spent       — a lesson's allowance ran out and the sale card appeared (mid-funnel)
 *   store click       — a store badge was tapped: they left for the App Store or Play (the goal)
 *
 * Snapchat is sent only the first and last of those, by choice — the two ends are what its campaign
 * is bid on, and a network is never handed an event nobody reads (specs/analytics-ads.md §4).
 */
import { isWebDemo } from '@/game/clip';
import { isDevEnv } from '@/game/dev';
import { platform } from '@/platform';

/** The ad networks we run campaigns on. One tag each, never two at once. */
export type AdNetwork = 'tiktok' | 'snapchat' | 'google';

/**
 * GA4 measurement id for the web demo's funnel — the Firebase-linked property on project
 * `abcshooter` (specs/analytics.md lists both properties that exist and why this is the one).
 *
 * Committed like the tag ids below, for the same reason: a measurement id is not a secret, and a
 * release built somewhere the env var happened to be missing would silently measure nothing.
 *
 * Setting this to an empty string is the off switch: the chunk is then never fetched and a demo
 * session behaves exactly as it did before GA4 existed.
 */
export const GA4_MEASUREMENT_ID = 'G-9SNCKSET2E';

/**
 * TikTok pixel id, from TikTok Ads Manager (Assets → Events → Web Events).
 *
 * Committed rather than kept in `.env` on purpose. A pixel id is not a secret — it ships inside
 * the page of every site that runs one — and the alternative fails in the worse direction: a
 * release built anywhere the env var happened to be missing would silently carry no tag, and the
 * campaign would look like it converted nobody.
 */
export const TIKTOK_PIXEL_ID = 'D9T0VKJC77UBO1NUPGRG';

/**
 * Snap Pixel id, from Snapchat Ads Manager (Events Manager → the web pixel).
 *
 * Committed for the same reason as TikTok's above: a pixel id ships inside the page of every site
 * that runs one, and a build that silently lost it would make the campaign look like it converted
 * nobody.
 */
export const SNAP_PIXEL_ID = 'b3776295-1ffb-40a2-be05-53515cf5ddf7';

/**
 * Google Ads conversion id (`AW-XXXXXXXXX`) and the conversion labels that go with it, from Google
 * Ads → Goals → Conversions → the action's tag setup.
 *
 * **Empty until the Google campaign is set up.** With no id the Google branch is inert: the chunk
 * is never fetched and `utm_source=google` behaves like an untagged visit. Filling these in is the
 * only step needed to switch that campaign on.
 */
export const GOOGLE_ADS_ID = '';

/**
 * Conversion labels per event. Google Ads only counts an event as a conversion when it carries the
 * label of a conversion action you defined, so a missing label means that event is reported as a
 * plain interaction and cannot be bid on. `storeClick` is the one that matters — it is the closest
 * thing the web has to "this ad produced a buyer".
 */
export const GOOGLE_CONVERSION_LABELS: Record<'trialSpent' | 'storeClick', string> = {
  trialSpent: '',
  storeClick: '',
};

/**
 * Whatever a loaded tag does with our conversion events.
 *
 * `trialSpent` is optional because Snapchat is deliberately sent only the two ends of the funnel
 * (specs/analytics-ads.md §4). A network that does not want an event says so by leaving the field
 * off, rather than every network having to supply a no-op for the ones it does not use.
 */
interface Tracker {
  trialSpent?: (packKey: string) => void;
  storeClick: (store: string) => void;
}

/**
 * The **ad networks'** tags that actually loaded — never GA4, which has its own sinks below.
 *
 * Keeping them apart is what stops the two audiences drifting into one. GA4 is fed by every web
 * session; this list is fed only by a demo session that an ad actually sent, and at most one entry
 * ever goes in it. Pushing GA4 in here "to simplify" would quietly start reporting organic
 * traffic to whichever network happened to be loaded.
 */
const adTrackers: Tracker[] = [];

/**
 * The hosts GA4 may measure. The ad tags are narrower still — `isDemoSession()` is web-only, so the
 * TWA never loads one even with `?demo` in its URL.
 */
const MEASURED_PLATFORMS: ReadonlySet<string> = new Set(['web', 'android']);

/**
 * localStorage flag for `app_first_open`. App-specific on purpose: the TWA shares the origin's
 * localStorage with Chrome on the same device, and a key the web build never writes means an
 * earlier browser visit cannot suppress the install's first open.
 */
export const APP_FIRST_OPEN_KEY = 'abcs.analytics.appFirstOpen.v1';

/** Set by the first initAnalytics() call, so a session is never counted twice. */
let started = false;

/**
 * Whether a round has already started this page load — the `first_round` parameter on
 * `round_start`, and nothing else reads it.
 *
 * Page-load scope, not GA4-session scope: a module-level flag is reset by a reload and by nothing
 * else, while GA4's session survives one and times out after thirty idle minutes. The two only
 * disagree for a visitor who reloads mid-session or leaves the tab open through a timeout, and of
 * the two definitions this is the one the funnel wants — `ready` is per page load as well, so
 * `ready → first round` compares like with like.
 */
let roundStarted = false;

function env(name: string): string {
  return (import.meta.env[name] ?? '').trim();
}

function tiktokId(): string {
  // An empty or whitespace override counts as "not set", not as "no tag" — .env.example ships the
  // keys with empty values, and copying it must not silently disable measurement.
  return env('VITE_TIKTOK_PIXEL_ID') || TIKTOK_PIXEL_ID;
}

function snapId(): string {
  return env('VITE_SNAP_PIXEL_ID') || SNAP_PIXEL_ID;
}

function googleId(): string {
  return env('VITE_GOOGLE_ADS_ID') || GOOGLE_ADS_ID;
}

function ga4Id(): string {
  return env('VITE_GA4_MEASUREMENT_ID') || GA4_MEASUREMENT_ID;
}

function googleLabels(): Record<'trialSpent' | 'storeClick', string> {
  return {
    trialSpent: env('VITE_GOOGLE_ADS_TRIAL_LABEL') || GOOGLE_CONVERSION_LABELS.trialSpent,
    storeClick: env('VITE_GOOGLE_ADS_STORE_LABEL') || GOOGLE_CONVERSION_LABELS.storeClick,
  };
}

/**
 * May this session be measured at all? A browser or the Android TWA — not the iOS app or Clip.
 *
 * Deliberately asks the platform and never the URL. `?demo` reaching the iOS WebView by any route
 * must not switch measurement on there, and an ordinary visitor who never asked for anything must
 * not be excluded from the numbers the free web build exists to produce.
 */
export function mayMeasure(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  return MEASURED_PLATFORMS.has(platform().platform);
}

/**
 * Is this the advertising demo? Narrower than mayMeasure(), and narrower than isClipMode() — see
 * the header. Decides the ad tags, and the `demo` parameter on every engagement event.
 */
export function isDemoSession(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  return isWebDemo();
}

/**
 * Which network sent this visitor, or null for "no tag".
 *
 * `utm_source` is the authority: every ad platform can set it on the final URL, we control it, and
 * it survives being pasted around. The click ids are a fallback for when auto-tagging is on but
 * the UTM was stripped — `ttclid` is TikTok's, `ScCid` Snapchat's, `gclid` Google's, and all three
 * are appended by the platform itself at click time.
 *
 * The click ids are matched case-insensitively, which is not pedantry: Snapchat's really is spelled
 * `ScCid`, and an exact-match lookup for one spelling would drop every visitor whose link carried
 * the other.
 */
export function adNetwork(): AdNetwork | null {
  if (typeof location === 'undefined') return null;
  const params = new URLSearchParams(location.search);

  const source = (params.get('utm_source') ?? '').trim().toLowerCase();
  if (source === 'tiktok') return 'tiktok';
  // Snapchat's own macros write `snapchat`; `snap` is what a hand-built link tends to say.
  if (source === 'snapchat' || source === 'snap') return 'snapchat';
  if (source === 'google') return 'google';

  const clickIds = new Set([...params.keys()].map(k => k.toLowerCase()));
  if (clickIds.has('ttclid')) return 'tiktok';
  if (clickIds.has('sccid')) return 'snapchat';
  if (clickIds.has('gclid')) return 'google';

  return null;
}

/**
 * GA4's own sinks, set once its chunk is in. Separate from `adTrackers` on two counts: GA4 hears
 * from every web session and an ad network only from the demo, and the engagement events go to our
 * property **only** — an ad network gets the three conversion events it can bid on and nothing
 * else, which is both the deal in specs/analytics-ads.md §4 and the reason a network never learns
 * how far into a lesson somebody got.
 */
let ga4: Tracker | null = null;
let ga4Event: ((name: string, params?: Record<string, unknown>) => void) | null = null;

/** Our own property, on every web session — the funnel, whoever sent the visitor and wherever to. */
async function loadGa4(): Promise<void> {
  const id = ga4Id();
  if (id === '') return;   // switched off — see GA4_MEASUREMENT_ID
  const mod = await import('./ga4');
  // Staging ships the same measurement id as production (it is a committed constant and deploy.sh
  // sets no override), so our own testing would otherwise be counted as real visitors. Dev and
  // staging hits are marked debug and land in DebugView instead of the reports.
  mod.loadTag(id, isDevEnv());
  ga4 = { trialSpent: mod.trackTrialSpent, storeClick: mod.trackStoreClick };
  ga4Event = mod.trackEvent;
  trackAppFirstOpen();
}

/**
 * The installed app's first launch — the event an install campaign is judged on (import it into
 * Google Ads as a conversion from GA4). Web visits never send it.
 *
 * Fires once per install: the flag is written when the event is handed to gtag. Offline, the GA4
 * chunk (kept out of the precache) does not load, so this never runs and the next online launch
 * sends it instead. The one loss: chunk served from the HTTP cache while gtag.js cannot load — the
 * flag is set and that install goes uncounted. Storage blocked → it may repeat, which over-counts
 * a few installs rather than throwing in a path the player could see.
 */
function trackAppFirstOpen(): void {
  if (platform().platform !== 'android') return;
  try {
    if (localStorage.getItem(APP_FIRST_OPEN_KEY) !== null) return;
    localStorage.setItem(APP_FIRST_OPEN_KEY, new Date().toISOString());
  } catch { /* storage blocked — send anyway */ }
  trackWeb('app_first_open');
}

/**
 * The tag of whichever network sent this visitor, if one did. Never two, never the wrong one — and
 * never outside the demo, however ordinary web measurement widens around it.
 */
async function loadAdTag(): Promise<void> {
  if (!isDemoSession()) return;   // organic traffic is ours; no network paid for it

  const network = adNetwork();
  if (network === null) return;   // untagged demo link: no ad tag at all

  if (network === 'tiktok') {
    const mod = await import('./tiktokPixel');
    mod.loadPixel(tiktokId());
    adTrackers.push({ trialSpent: mod.trackTrialSpent, storeClick: mod.trackStoreClick });
    return;
  }

  if (network === 'snapchat') {
    const snap = snapId();
    if (snap === '') return;
    const mod = await import('./snapPixel');
    mod.loadPixel(snap);
    // No `trialSpent`: Snapchat gets the visit and the store tap, and nothing in between.
    adTrackers.push({ storeClick: mod.trackStoreClick });
    return;
  }

  const id = googleId();
  if (id === '') return;   // campaign not set up yet — see GOOGLE_ADS_ID
  const mod = await import('./googleTag');
  mod.loadTag(id, googleLabels());
  adTrackers.push({ trialSpent: mod.trackTrialSpent, storeClick: mod.trackStoreClick });
}

/**
 * Load whatever this session is allowed, and count the visit. Call once at startup; a second call
 * does nothing, so a session is never counted twice.
 *
 * Fire-and-forget by design: nothing in the game waits on measurement, and a blocked or failed
 * request must never keep the menu from appearing. The two loads are caught separately — an ad
 * blocker eating one chunk (they are blocked by name) must not take the other down with it.
 *
 * GA4 goes first, and not only for tidiness: its cookieless consent default has to reach the
 * dataLayer before any other Google tag configures itself.
 */
export async function initAnalytics(): Promise<void> {
  if (!mayMeasure() || started) return;
  started = true;

  for (const load of [loadGa4, loadAdTag]) {
    try {
      await load();
    } catch {
      /* offline, blocked by an extension, chunk 404 after a redeploy — measurement is not worth an
         error path the player can see. */
    }
  }
}

/**
 * A lesson's free rounds ran out and the sale card is on screen.
 *
 * Only the demo has a capped trial, so in practice this is a demo-only event whichever gate it is
 * given (src/game/ui/clipOutro.ts). Both gates are still applied, in the order that matters: our
 * property first, the ad network second and only if one paid for the visit.
 */
export function trackTrialSpent(packKey: string): void {
  if (!mayMeasure()) return;
  ga4?.trialSpent?.(packKey);
  if (!isDemoSession()) return;
  for (const t of adTrackers) t.trialSpent?.(packKey);
}

/**
 * A store badge was tapped: they left to buy the full app.
 *
 * The badges show on the free web build too (src/game/ui/storeBadges.ts), so widening the gate
 * makes this the first measurement of a conversion surface that was previously invisible — the
 * ordinary visitor who came to play and chose to buy. The ad networks still only hear about it
 * when the session was theirs.
 */
export function trackStoreClick(store: 'appstore' | 'googleplay'): void {
  if (!mayMeasure()) return;
  ga4?.storeClick(store);
  if (!isDemoSession()) return;
  for (const t of adTrackers) t.storeClick(store);
}

/**
 * The engagement events — GA4 only, never an ad network.
 *
 * Each one still re-checks the gate, so every call site below can stay a plain unconditional call:
 * these fire from scenes the App Clip and the iOS app run too, and the whole point of the gate
 * living here is that no caller has to remember that. Inside those they cost one boolean.
 *
 * **`app_platform`** rides on every one as well (`web` | `android`), so the reports can split the
 * shop window from the installed app without a second set of event names.
 *
 * **`demo` rides on every one of them**, and as of 29 August 2026 it finally varies. It was written
 * as a constant `true` on 27 August precisely so this day would be cheap: the event names say what
 * happened (`round_start`) rather than where it happened, so widening measurement to the whole free
 * web build splits the reports on a parameter instead of needing a second set of event names and a
 * break in the history. `demo:true` is still the advertising trial; `demo:false` is somebody who
 * just came to play.
 */
function trackWeb(name: string, params: Record<string, unknown> = {}): void {
  if (!mayMeasure()) return;
  ga4Event?.(name, { demo: isDemoSession(), app_platform: platform().platform, ...params });
}

/**
 * The game finished loading and the menu is on screen. Fires once per page load, and is what
 * separates "the tag fired" from "somebody actually got in" — a `page_view` without this one is a
 * visitor who left, or a device the bundle never finished loading on.
 */
export function trackReady(): void {
  trackWeb('ready');
}

/**
 * Play was tapped: the visitor is not merely looking at the menu.
 *
 * `first_round` is what makes this event answerable two ways at once. It fires from
 * `RoundScene.create`, which runs on **every** round, so the raw count is rounds played, not
 * players who played — a visitor who starts five games sends five. That is the number worth having
 * for "do they come back for another go", and the wrong one for "what share of the menu converted
 * into a game", which is the top of the funnel and the thing an ad campaign is judged on.
 *
 * So the parameter marks the first round of the page load, and the reports get both: filter
 * `first_round = true` for one-per-visitor, leave it off for rounds played. It also gives GA4 a
 * clean condition for a derived key event (Admin → Events → Create event), which is the point —
 * marking `round_start` itself as a key event would count every replay as a conversion.
 */
export function trackRoundStart(packKey: string): void {
  const first = !roundStarted;
  roundStarted = true;
  trackWeb('round_start', { content_id: packKey, first_round: first });
}

/** A board was cleared. `board_number` is how deep into the capped trial they got. */
export function trackBoardCleared(packKey: string, boardNumber: number): void {
  trackWeb('board_cleared', { content_id: packKey, board_number: boardNumber });
}

/**
 * A rung of the in-game planet ladder lit up (src/game/milestones.ts). Fires at most once per
 * level per game, so the count is "games that got this far", not "answers scored after getting
 * there".
 */
export function trackPlanetReached(packKey: string, planet: string, stars: number): void {
  trackWeb('planet_reached', { content_id: packKey, planet, stars });
}

/**
 * The round is over. Three things at once, which is why it is the most useful row in the property:
 *
 *  - `reason` — `lives_out` means the game beat them, `completed` means they ran out of boards,
 *    which in a demo session is the trial cap.
 *  - `board_number` — how many boards the game lasted, i.e. roughly how long they played.
 *  - `stars` / `planet` — how far up the ladder they got before it ended. `planet` is the furthest
 *    *named* rung, so a game that stops at 10 stars still reports `sun` rather than nothing.
 */
export function trackRoundEnd(
  packKey: string, boardNumber: number, reason: string, stars: number, planet: string | null,
): void {
  trackWeb('round_end', {
    content_id: packKey,
    board_number: boardNumber,
    reason,
    stars,
    planet: planet ?? 'none',
  });
}

/** A survival run started — the UFO mini-game, reachable in the demo by earning Mars. */
export function trackSurvivalStart(): void {
  trackWeb('survival_start');
}

/** A survival run ended, with the score it reached. */
export function trackSurvivalEnd(score: number): void {
  trackWeb('survival_end', { score });
}

/** A Pairs run started — a Diamond was spent (specs/pairs-mode.md §10). */
export function trackPairsStart(): void {
  trackWeb('pairs_start');
}

/**
 * A Pairs run ended, with the pairs it found.
 *
 * Wired exactly like survival_start/end above, and expected to report **zero** for now: the
 * Diamond is earned at Saturn (star 6) and a board-capped demo run reaches Mercury. It exists so
 * the mode is measurable the moment that cap changes, rather than being the one mini-game that
 * silently reports nothing.
 */
export function trackPairsEnd(pairsFound: number): void {
  trackWeb('pairs_end', { score: pairsFound });
}

/** An Ice Maze run started — an Ice Cube was spent (specs/word-maze-mode.md §10). */
export function trackMazeStart(): void {
  trackWeb('maze_start');
}

/**
 * An Ice Maze run ended, with the words it found. Expected to report **zero** for the same reason
 * pairs_end is: the Ice Cube is earned at Neptune (star 8) and a board-capped demo run reaches
 * Mercury. It exists so the mode is measurable the moment that cap changes.
 */
export function trackMazeEnd(wordsFound: number): void {
  trackWeb('maze_end', { score: wordsFound });
}

/** A Word Wagon run started — a Galaxy was spent (specs/wagon-mode.md §11). */
/** A Satellite was spent — a Word Labyrinth run opened (specs/labyrinth-mode.md §11). */
export function trackLabyrinthStart(): void {
  trackWeb('labyrinth_start');
}

/** A Word Labyrinth run ended; `score` is rounds cleared. */
export function trackLabyrinthEnd(rounds: number): void {
  trackWeb('labyrinth_end', { score: rounds });
}

/** A Plate was spent — a Recipe Kitchen run opened (specs/recipe-mode.md §2). */
export function trackRecipeStart(): void {
  trackWeb('recipe_start');
}

/** A Recipe Kitchen run ended; `score` is dishes served. */
export function trackRecipeEnd(dishes: number): void {
  trackWeb('recipe_end', { score: dishes });
}

export function trackRocketStart(): void {
  trackWeb('rocket_start');
}

/**
 * One Rocket Read round (specs/rocket-read-mode.md §8). `read_ms / words` on a tapped, right round
 * is a real child's reading speed — the data that tunes the adaptive clock. `words` is the raw
 * count, `units` the language-scaled one the budget was computed from (rocket/clock.ts).
 */
export function trackRocketRound(p: {
  round: number; words: number; units: number; budgetMs: number; readMs: number;
  timedOut: boolean; correct: boolean; answerKind: string;
}): void {
  trackWeb('rocket_round', {
    round: p.round, words: p.words, units: p.units, budget_ms: p.budgetMs, read_ms: p.readMs,
    timed_out: p.timedOut, correct: p.correct, answer_kind: p.answerKind,
  });
}

/** A Rocket Read run ended; `score` is the round reached. */
export function trackRocketEnd(round: number): void {
  trackWeb('rocket_end', { score: round });
}

export function trackWagonStart(): void {
  trackWeb('wagon_start');
}

/**
 * A Word Wagon run ended, with the loads it shipped. Expected to report zero more reliably than
 * any other mode: the Galaxy is earned at the top of the planet ladder (213 correct smashes) and a
 * board-capped demo run reaches Mercury. It exists so the mode is measurable the moment that cap
 * changes.
 */
export function trackWagonEnd(loads: number): void {
  trackWeb('wagon_end', { score: loads });
}

/**
 * The lesson itself was completed — every word mastered, which is the end of the journey rather
 * than the end of one game.
 */
export function trackLessonFinished(packKey: string): void {
  trackWeb('lesson_finished', { content_id: packKey });
}

/** They switched lesson from the menu — which lesson a demo visitor actually wants. */
export function trackLessonChange(packKey: string): void {
  trackWeb('lesson_change', { content_id: packKey });
}

/**
 * A day counted toward the streak (specs/day-streak.md §4.4). Only the BUCKET of the length goes
 * out, never the raw number, and nothing identifies the child or the date.
 */
export function trackStreakDay(kind: string, bucket: string): void {
  trackWeb('streak_day', { kind, streak_len_bucket: bucket });
}

/** They switched language from the menu. */
export function trackLanguageChange(lang: string): void {
  trackWeb('language_change', { lang });
}
